import { useEffect, useRef } from 'react';
import { resolveShareUrl } from '../../Admin/Services/SupportService';

/**
 * Forwards a visitor who opens a share URL - a display name such as
 * `http://local-nexgent.tech/` - to the destination it was mapped to (the URL the admin
 * sees in the form's Preview).
 *
 * Runs once per full page load: the current address is asked of the public resolver, and a
 * match is replaced with the stored redirect URL, which keeps the share URL out of the
 * history. A miss is the normal case - most loads are not share URLs - and costs one lookup
 * that finds nothing.
 *
 * Only fires when the display name actually opens this application, since that is what loads
 * this code. A display name pointing at another domain is that domain's to redirect; nothing
 * here runs in that case.
 */
const ShareUrlRedirect = () => {
  const checked = useRef(false);

  useEffect(() => {
    if (checked.current) return;
    checked.current = true;

    const current = window.location.href.split('#')[0];

    resolveShareUrl(current)
      .then((response) => {
        const target = response.data?.url;
        if (!response.success || !target) return;
        // The backend refuses a self-referential redirect too; this keeps a browser-normalised
        // spelling of the same address from bouncing once before the loop guard would catch it.
        const strip = (value: string) => value.replace(/\/+$/, '');
        if (strip(target) === strip(current)) return;
        window.location.replace(target);
      })
      .catch(() => {
        // Not a share URL, or the resolver is unreachable - the page just stays as it is.
      });
  }, []);

  return null;
};

export default ShareUrlRedirect;
